// Webpack asset import for worklet file
// @ts-ignore - webpack asset module
import audioProcessorWorkletUrl from "./AudioProcessorWorklet.js";

export class AudioProcessor {
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private audioStream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private analyserData: Uint8Array | null = null;
  private isProcessing = false;
  private onAudioProcessedCallback: ((data: string) => void) | null = null;

  get node() {
    return this.workletNode;
  }

  async init(): Promise<void> {
    try {
      console.log("Initializing AudioProcessor (web)");
      const audioContext = new AudioContext({ sampleRate: 24000 });
      this.audioContext = audioContext;

      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      this.audioStream = audioStream;

      this.sourceNode = audioContext.createMediaStreamSource(audioStream);

      await audioContext.audioWorklet.addModule(audioProcessorWorkletUrl);

      this.workletNode = new AudioWorkletNode(
        audioContext,
        "audio-processor",
      );

      this.analyserNode = audioContext.createAnalyser();
      this.analyserNode.fftSize = 256;
      this.analyserNode.smoothingTimeConstant = 0.4;
      this.analyserData = new Uint8Array(this.analyserNode.frequencyBinCount);

      this.sourceNode.connect(this.analyserNode);
      this.analyserNode.connect(this.workletNode);

      this.workletNode.port.onmessage = (event) => {
        if (
          event.data.type === "audio-processed" &&
          this.onAudioProcessedCallback &&
          this.isProcessing
        ) {
          this.onAudioProcessedCallback(btoa(event.data.audioData));
        }
      };
    } catch (error) {
      console.error("Error initializing AudioProcessor:", error);
      throw error;
    }
  }

  /** Returns the current mic input level as a 0–1 float. */
  getMicLevel(): number {
    if (!this.analyserNode || !this.analyserData) return 0;
    this.analyserNode.getByteFrequencyData(this.analyserData);
    let sum = 0;
    for (let i = 0; i < this.analyserData.length; i++) {
      sum += this.analyserData[i];
    }
    return sum / this.analyserData.length / 255;
  }

  start(onAudioProcessed: (data: string) => void): void {
    if (this.isProcessing) return;

    // Resume AudioContext if suspended (browser autoplay policy requires user gesture)
    if (this.audioContext?.state === "suspended") {
      this.audioContext.resume();
    }

    this.onAudioProcessedCallback = onAudioProcessed;
    this.isProcessing = true;

    if (this.workletNode) {
      this.workletNode.port.postMessage({
        type: "start-processing",
      });
    }
  }

  stop(): void {
    console.log("Stopping AudioProcessor", { isProcessing: this.isProcessing });
    if (!this.isProcessing) return;

    if (this.workletNode) {
      this.workletNode.port.postMessage({
        type: "stop-processing",
      });
    }

    this.isProcessing = false;
    this.onAudioProcessedCallback = null;
  }

  disconnect(): void {
    this.stop();

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
      this.analyserData = null;
    }

    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    if (this.audioStream) {
      this.audioStream.getTracks().forEach((t) => t.stop());
      this.audioStream = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}
